import type { SidebarItemKey } from "../../config/sidebarConfig";
import type { CatalogueEntry } from "./catalogue";
import { isGreeting, normalise, stripLeadingGreeting } from "./smallTalk";

/** "What can you do?", "help", "how does this work?": one sentence asking what Ask Noticeboard is. */
const INTRODUCTION_REQUEST = new RegExp(
  "^(" +
    [
      "help( me| please)?",
      "i need help",
      "can you help( me)?",
      "what (can|do) you do",
      "what else can you do",
      "what features do you have",
      "what are your features",
      "which features do you have",
      "what can you help( me)? with",
      "what (can|should) i ask( you)?",
      "what are you",
      "who are you",
      "how does (this|it) work",
      "how do i use (this|you)",
      "what is noticeboard",
      "what can i use (it|this|noticeboard) for",
    ].join("|") +
    ")$"
);

/**
 * Asking what Ask Noticeboard is, and nothing else: every sentence asks it
 * ("What can you do? What can I use it for?"), perhaps after a greeting ("hi, what can you do?",
 * or "hi there what can you do?" with no comma). "What is Noticeboard?" on its own asks about the
 * product, so the catalogue answers it.
 */
export function isIntroductionRequest(text: string): boolean {
  const sentences = text
    .split(/[?.!,;]+/)
    .map((part) => stripLeadingGreeting(normalise(part)))
    .filter((said) => said && !isGreeting(said));
  if (sentences.length === 1 && sentences[0] === "what is noticeboard") return false;
  return sentences.length > 0 && sentences.every((said) => INTRODUCTION_REQUEST.test(said));
}

/** What can be found by typing its name, by the sidebar item that has to open for it. */
const FINDABLE: Array<[SidebarItemKey, string]> = [
  ["notices", "Notices"],
  ["users", "Users"],
  ["circles", "Circles"],
  ["drive", "files"],
  ["helpCenter", "help articles"],
];

/** The tasks it can do, by the entry that starts them; a Poll is a kind of Notice. */
const DOABLE: Array<[string, string[]]> = [
  ["create.notice", ["a Notice", "a Poll"]],
  ["calendar.createEvent", ["an Event"]],
];

/** Example questions to offer, best first; the first this person may ask is used. */
const ORGANIZATION_QUESTIONS = ["How many parents haven't joined yet?", "How many notices did we send this week?"];
const HOW_TO_QUESTIONS = ["How do I schedule a notice?"];
const TASK_REQUESTS = ["Send a notice", "Create an event"];

/** "a, b and c" */
const listOf = (items: string[], and = "and") =>
  items.length > 1 ? `${items.slice(0, -1).join(", ")} ${and} ${items[items.length - 1]}` : items[0];

/**
 * A short introduction to Ask Noticeboard, and example questions to press. It
 * mentions only what `available` (the entries this person may ask about) can
 * open, and each example is one of those entries' examples word for word.
 */
export function introductionFor(available: CatalogueEntry[]): { text: string; questions: string[] } {
  const canAsk = (q: string) => available.some((e) => e.examples.includes(q));
  const firstAskable = (qs: string[]) => qs.find(canAsk);
  const opens = (key: SidebarItemKey) => available.some((e) => e.sidebarItem === key);

  const findable = FINDABLE.filter(([key]) => opens(key)).map(([, name]) => name);
  const organizationQuestion = firstAskable(ORGANIZATION_QUESTIONS);
  const howToQuestion = firstAskable(HOW_TO_QUESTIONS);
  const doable = DOABLE.filter(([id]) => available.some((e) => e.id === id)).flatMap(([, names]) => names);

  const lines = [
    findable.length > 0 && `• Find ${listOf(findable)}: start typing a name.`,
    organizationQuestion && `• Answer questions about your Organization, like "${organizationQuestion}"`,
    howToQuestion && `• Explain how to do things, like "${howToQuestion}"`,
    doable.length > 0 && `• Do things for you: create ${listOf(doable, "or")}. You check it before anything is sent.`,
  ].filter((line): line is string => Boolean(line));

  return {
    text: ["I'm Ask Noticeboard. I can:", ...lines, "Try one of these:"].join("\n"),
    questions: [organizationQuestion, howToQuestion, ...TASK_REQUESTS.filter(canAsk)].filter(
      (q): q is string => Boolean(q)
    ),
  };
}
